import React, { useEffect, useState, useRef } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar/Sidebar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import { useLocation } from "react-router-dom";
import { ROUTE, BECHAT_WIDGET_URL } from "utils/constants";
import { useWorker } from "hooks/useWorker";

/**
 * Hash email using HMAC SHA256, similar to PHP's hash_hmac('sha256', email, secret)
 * @param {string} email - Email to hash
 * @param {string} secret - Secret key for HMAC
 * @returns {Promise<string>} - Hex-encoded hash string
 */
const hashEmail = async (email, secret) => {
  if (!email || !secret) {
    return null;
  }

  try {
    // Convert secret to ArrayBuffer
    const encoder = new TextEncoder();
    const secretKey = encoder.encode(secret);
    const emailData = encoder.encode(email);

    // Import secret as a key for HMAC
    const cryptoKey = await crypto.subtle.importKey(
      "raw",
      secretKey,
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"]
    );

    // Sign (hash) the email
    const signature = await crypto.subtle.sign("HMAC", cryptoKey, emailData);

    // Convert ArrayBuffer to hex string
    const hashArray = Array.from(new Uint8Array(signature));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");

    return hashHex;
  } catch (error) {
    console.error("Error hashing email:", error);
    return null;
  }
};

const buildBeChatUserData = async (worker = {}) => {
  const { id, email, first_name, last_name, phone_number } = worker;
  const name = [first_name, last_name].filter(Boolean).join(" ").trim();
  
  // Get secret from environment variable, with fallback to default
  const secret = process.env.REACT_APP_BECHAT_WIDGET_SECRET || 'NE5wdzVEQm9CeUVMTJRtcANEwXxuVjFaeVQ0QmJTQUC';
  
  // Hash email if email exists
  let emailHash = null;
  if (email) {
    emailHash = await hashEmail(email, secret);
  }

  const userData = {
    id,
    email,
    // CRITICAL: Ensure name is always set if email is set
    // This prevents "Visitor" from showing in widget
    name: name || email || undefined,
    phone_number,
    platform: 'Pharmacy Portal', // Platform identifier for rxdn_web_portal
    ...(emailHash && { email_hash: emailHash }),
  };

  Object.keys(userData).forEach((key) => {
    if (userData[key] === undefined || userData[key] === null || userData[key] === "") {
      delete userData[key];
    }
  });

  return userData;
};

const Layout = ({ children }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isPop, setIsPop] = useState(false);
  const beChatInitializedRef = useRef(false);
  const lastUserDataRef = useRef(null);

  const worker = useWorker() || {};
  const { isLogged, id, email, first_name, last_name, phone_number } = worker;

  const location = useLocation();
  const { pathname } = location;

  useEffect(() => {
    if (window.innerWidth <= 600) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  }, []);
  
  const onClickDrawerOpen = () => {
    setIsOpen(!isOpen);
  };

  const onClickPopupOpen = () => {
    setIsPop(true);
  }

  const handlePopupClose = () => {
    setIsPop(false);
  }

  const isExtendLayout =
    pathname !== ROUTE.SIGN_IN.URL &&
    pathname !== ROUTE.SIGN_UP.URL &&
    pathname !== ROUTE.RESET_PASSWORD.URL &&
    pathname !== "/404";

  // Set window.BeChatSettings.user BEFORE script loads
  // This ensures email is included in iframe URL params
  useEffect(() => {
    if (typeof window === "undefined") return;

    const shouldShowChat = isExtendLayout && Boolean(isLogged);
    const currentEmail = email || '';
    const lastEmail = localStorage.getItem('bechat:lastEmail') || '';
    const identityChanged = currentEmail !== lastEmail;

    // Store current email in localStorage to track identity changes
    if (shouldShowChat && currentEmail) {
      localStorage.setItem('bechat:lastEmail', currentEmail);
    } else {
      localStorage.removeItem('bechat:lastEmail');
    }

    // Initialize BeChatSettings with widgetDomain
    if (!window.BeChatSettings) {
      window.BeChatSettings = {
        widgetDomain: BECHAT_WIDGET_URL,
        // Always force identity to avoid leaking previous user on logout
        forceUser: false, // Will be set to true if user has email
      };
    } else if (!window.BeChatSettings.widgetDomain) {
      window.BeChatSettings.widgetDomain = BECHAT_WIDGET_URL;
    }

    if (shouldShowChat) {
      buildBeChatUserData({
        id,
        email,
        first_name,
        last_name,
        phone_number,
      }).then((userData) => {
        const userDataString = JSON.stringify(userData);
        const lastUserDataString = lastUserDataRef.current ? JSON.stringify(lastUserDataRef.current) : null;
        
        // Only update if user data changed
        if (userDataString !== lastUserDataString || identityChanged) {
          window.BeChatSettings.user = userData;
          // CRITICAL: Set forceUser to true if user has email
          // This ensures identity is forced even if enforce_hmac is enabled
          window.BeChatSettings.forceUser = Boolean(userData?.email);
          lastUserDataRef.current = userData;
          
          console.log("BeChat: Set window.BeChatSettings.user", userData);
          console.log("BeChat: forceUser =", window.BeChatSettings.forceUser);
          
          // If identity changed, reset widget to ensure new identity is used
          if (identityChanged && window.BeChat) {
            console.log("BeChat: Identity changed, resetting widget");
            window.BeChat.destroy().then(() => {
              setTimeout(() => {
                loadBeChatScript();
              }, 100);
            });
          } else {
            // If iframe already exists but doesn't have email in URL, reload it
            const iframe = document.getElementById('be-chat-iframe');
            if (iframe && window.BeChat) {
              const iframeUrl = new URL(iframe.src);
              const hasUserParam = iframeUrl.searchParams.has('user');
              
              // If iframe doesn't have user param but we have user data, reload iframe
              if (!hasUserParam && userData && Object.keys(userData).length > 0) {
                console.log("BeChat: Reloading iframe with user data");
                window.BeChat.destroy().then(() => {
                  // Script will recreate iframe with new user data from window.BeChatSettings
                  setTimeout(() => {
                    loadBeChatScript();
                  }, 100);
                });
              }
            }
          }
        }
      });
    } else {
      // Clear user data when not logged in
      if (window.BeChatSettings) {
        delete window.BeChatSettings.user;
        window.BeChatSettings.forceUser = false;
      }
      lastUserDataRef.current = null;
      
      // Clear BeChat storage when logged out
      if (window.BeChat) {
        try {
          window.BeChat.destroy();
        } catch (e) {
          // ignore errors
        }
      }
      
      // Clear BeChat-related localStorage
      const prefixes = ['bechat', 'be-chat', 'be_chat', 'bechat:', 'be-chat:'];
      const shouldClear = (key) => {
        const k = key.toLowerCase();
        return prefixes.some((p) => k.startsWith(p));
      };
      Object.keys(localStorage).forEach((key) => {
        if (shouldClear(key)) {
          localStorage.removeItem(key);
        }
      });
    }
  }, [isExtendLayout, isLogged, id, email, first_name, last_name, phone_number]);

  // Function to load BeChat script dynamically
  const loadBeChatScript = (forceReload = false) => {
    const loaderId = 'bechat-loader';
    const existingScript = document.getElementById(loaderId);
    
    // Check if script already exists
    if (existingScript && !forceReload) {
      console.log("BeChat: Script already exists, skipping load");
      // But check if iframe exists - if not, we need to reload script
      setTimeout(() => {
        const iframe = document.getElementById('be-chat-iframe');
        if (!iframe) {
          console.warn("BeChat: Script exists but iframe doesn't - forcing reload");
          // Remove old script and reload
          existingScript.remove();
          loadBeChatScript(true);
        }
      }, 1000);
      return;
    }
    
    // Remove existing script if force reload
    if (existingScript && forceReload) {
      console.log("BeChat: Removing existing script for force reload");
      existingScript.remove();
      // Also remove existing iframe if any
      const existingIframe = document.getElementById('be-chat-iframe');
      if (existingIframe) {
        existingIframe.remove();
      }
    }
    
    console.log("BeChat: Creating script element", `${BECHAT_WIDGET_URL}/livechat-loader.js`);
    const script = document.createElement('script');
    script.id = loaderId;
    // Add cache busting with timestamp to ensure fresh script is loaded
    script.src = `${BECHAT_WIDGET_URL}/livechat-loader.js?v=${Date.now()}`;
    script.async = true;
    script.onerror = () => {
      console.error("❌ BeChat script failed to load:", script.src);
      console.error("Please check:");
      console.error(`1. Is ${BECHAT_WIDGET_URL} accessible?`);
      console.error("2. Can you access the script directly in browser?");
      console.error("3. Check Network tab for errors");
    };
    script.onload = () => {
      console.log("✅ BeChat script loaded successfully, waiting for BeChat object...");
      console.log("BeChat: Document readyState:", document.readyState);
      console.log("BeChat: window.BeChatSettings:", window.BeChatSettings);
      
      // CRITICAL: executeLoader() in livechat-loader.js might not run if:
      // 1. Script loads when document.readyState === 'interactive' (not 'complete')
      // 2. DOMContentLoaded already fired, so listener won't trigger
      // Solution: Wait for document to be complete, then check if iframe exists
      const waitForCompleteAndCheck = () => {
        if (document.readyState === 'complete') {
          // DOM is complete, check if iframe exists
          setTimeout(() => {
            const iframe = document.getElementById('be-chat-iframe');
            if (!iframe && window.BeChat) {
              console.error("BeChat: Iframe still not created after DOM complete!");
              console.error("BeChat: executeLoader() might not have run");
              console.error("BeChat: Trying to manually trigger via showLauncher()...");
              
              // showLauncher() waits for isIframeReady, which should trigger executeLoader()
              // But if executeLoader() didn't run, we need to force it
              if (typeof window.BeChat.showLauncher === 'function') {
                window.BeChat.showLauncher().then(() => {
                  setTimeout(() => {
                    const iframe2 = document.getElementById('be-chat-iframe');
                    if (iframe2) {
                      console.log("BeChat: Iframe created after showLauncher()!", {
                        src: iframe2.src,
                        display: iframe2.style.display
                      });
                    } else {
                      console.error("BeChat: Iframe still not created - executeLoader() definitely didn't run");
                    }
                  }, 1000);
                }).catch(err => {
                  console.error("BeChat: Error in showLauncher()", err);
                });
              }
            } else if (iframe) {
              console.log("BeChat: Iframe exists!", {
                src: iframe.src,
                display: iframe.style.display
              });
            }
          }, 500);
        } else {
          // Wait for DOM to be complete
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', waitForCompleteAndCheck);
          } else {
            // interactive - wait a bit then check
            setTimeout(waitForCompleteAndCheck, 100);
          }
        }
      };
      
      waitForCompleteAndCheck();
    };
    document.body.appendChild(script);
    console.log("BeChat: Script element appended to body");
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const shouldShowChat = isExtendLayout && Boolean(isLogged);
    
    console.log("BeChat: useEffect triggered", {
      shouldShowChat,
      isExtendLayout,
      isLogged,
      email,
      widgetDomain: BECHAT_WIDGET_URL,
    });

    // Initialize BeChatSettings if not already set
    if (!window.BeChatSettings) {
      window.BeChatSettings = {
        widgetDomain: BECHAT_WIDGET_URL,
        // Always force identity to avoid leaking previous user on logout
        forceUser: false, // Will be set to true if user has email
      };
      console.log("BeChat: Initialized BeChatSettings", window.BeChatSettings);
    } else if (!window.BeChatSettings.widgetDomain) {
      window.BeChatSettings.widgetDomain = BECHAT_WIDGET_URL;
      console.log("BeChat: Updated widgetDomain", BECHAT_WIDGET_URL);
    }

    // CRITICAL: Set user data BEFORE loading script
    // This ensures iframe URL includes user data when executeLoader() runs
    if (shouldShowChat) {
      buildBeChatUserData({
        id,
        email,
        first_name,
        last_name,
        phone_number,
      }).then((userData) => {
        if (userData && Object.keys(userData).length > 0) {
          window.BeChatSettings.user = userData;
          window.BeChatSettings.forceUser = Boolean(userData?.email);
          console.log("BeChat: Set user data BEFORE script load", userData);
          console.log("BeChat: forceUser =", window.BeChatSettings.forceUser);
          
          // Now load script - it will use the user data we just set
          console.log("BeChat: Loading script with user data...");
          loadBeChatScript();
        } else {
          console.warn("BeChat: No user data to set, loading script anyway");
          loadBeChatScript();
        }
      }).catch((error) => {
        console.error("BeChat: Error building user data", error);
        loadBeChatScript();
      });
    } else {
      console.log("BeChat: Not loading script - shouldShowChat is false");
    }

    const syncBeChat = async (beChat) => {
      try {
        if (shouldShowChat) {
          const userData = await buildBeChatUserData({
            id,
            email,
            first_name,
            last_name,
            phone_number,
          });
          console.log("BeChat: Showing launcher", { userData, shouldShowChat, isLogged, isExtendLayout });
          if (userData && Object.keys(userData).length > 0) {
            await beChat.setUserData(userData);
            console.log("BeChat: User data set", userData);
          }
          
          // CRITICAL: showLauncher() will wait for isIframeReady, which triggers executeLoader()
          // But if executeLoader() hasn't run yet, we need to wait for it
          // Check if iframe exists - if not, executeLoader() might not have run yet
          let iframe = document.getElementById('be-chat-iframe');
          if (!iframe) {
            console.log("BeChat: Iframe not found yet, waiting for executeLoader() to create it...");
            // Wait for iframe to be created by executeLoader() (max 5 seconds)
            let retries = 0;
            while (!iframe && retries < 50) {
              await new Promise(resolve => setTimeout(resolve, 100));
              iframe = document.getElementById('be-chat-iframe');
              retries++;
              if (retries % 10 === 0) {
                console.log(`BeChat: Still waiting for iframe... (${retries * 100}ms)`);
              }
            }
            if (!iframe) {
              console.error("BeChat: Iframe still not found after waiting 5 seconds!");
              console.error("BeChat: This means executeLoader() in livechat-loader.js might not have run");
              console.error("BeChat: Checking window.BeChatSettings:", window.BeChatSettings);
              // Try to manually check if there's an error
              const scriptTag = document.querySelector('script[src*="livechat-loader"]');
              if (scriptTag) {
                console.error("BeChat: Script tag exists:", scriptTag.src);
              }
            } else {
              console.log("BeChat: Iframe found after waiting", retries * 100, "ms");
            }
          } else {
            console.log("BeChat: Iframe already exists before showLauncher()");
          }
          
          // showLauncher() will wait for isIframeReady internally
          await beChat.showLauncher();
          console.log("BeChat: Launcher shown");
          
          // Check iframe status after showing launcher
          setTimeout(() => {
            const iframe = document.getElementById('be-chat-iframe');
            if (iframe) {
              const computedStyle = window.getComputedStyle(iframe);
              console.log("BeChat: Iframe status:", {
                exists: true,
                display: iframe.style.display,
                computedDisplay: computedStyle.display,
                visibility: iframe.style.visibility,
                computedVisibility: computedStyle.visibility,
                width: computedStyle.width,
                height: computedStyle.height,
                src: iframe.src,
                zIndex: computedStyle.zIndex
              });
              
              // Force show if still hidden
              if (iframe.style.display === 'none' || computedStyle.display === 'none') {
                console.log("BeChat: Iframe is hidden, forcing to show");
                iframe.style.display = 'block';
                iframe.style.visibility = 'visible';
              }
            } else {
              console.warn("BeChat: Iframe not found in DOM after showLauncher!");
            }
          }, 500);
          
          // Fallback: Force show iframe if showLauncher didn't work
          // Try multiple times with increasing delays
          [1000, 2000, 3000, 5000, 7000].forEach((delay) => {
            setTimeout(() => {
              const iframe = document.getElementById('be-chat-iframe');
              if (iframe) {
                const computedStyle = window.getComputedStyle(iframe);
                if (iframe.style.display === 'none' || computedStyle.display === 'none') {
                  console.log(`BeChat: Fallback (${delay}ms) - forcing iframe to show`);
                  iframe.style.display = 'block';
                  iframe.style.visibility = 'visible';
                  iframe.style.zIndex = '2147483639';
                }
              } else {
                console.warn(`BeChat: Iframe not found at ${delay}ms delay`);
              }
            }, delay);
          });
        } else {
          // Only hide if BeChat is available and initialized
          if (beChat && typeof beChat.hideLauncher === "function") {
            console.log("BeChat: Hiding launcher", { shouldShowChat, isLogged, isExtendLayout });
            await beChat.hideLauncher();
          }
        }
      } catch (error) {
        console.error("BeChat error:", error);
        // ignore livechat errors to avoid breaking navigation
      }
    };

    // Observer to continuously monitor and force show iframe if it gets hidden
    let observerInterval = null;
    let styleTag = null;
    if (shouldShowChat) {
      // Add a style tag to force show the iframe with !important
      styleTag = document.createElement('style');
      styleTag.id = 'be-chat-force-show';
      styleTag.textContent = `
        #be-chat-iframe {
          display: block !important;
          visibility: visible !important;
        }
      `;
      document.head.appendChild(styleTag);

      observerInterval = setInterval(() => {
        const iframe = document.getElementById('be-chat-iframe');
        if (iframe) {
          const computedStyle = window.getComputedStyle(iframe);
          if (iframe.style.display === 'none' || computedStyle.display === 'none') {
            console.log("BeChat: Observer detected hidden iframe - forcing show");
            // Remove display: none from inline style
            iframe.style.removeProperty('display');
            iframe.style.removeProperty('visibility');
            // Force set display block
            iframe.style.setProperty('display', 'block', 'important');
            iframe.style.setProperty('visibility', 'visible', 'important');
          }
        }
      }, 500); // Check every 500ms for faster response
    }

    // Wait for BeChat to be available and loaded
    let timeoutId = null;
    const initBeChat = (retryCount = 0) => {
      const beChat = window.BeChat;
      if (!beChat) {
        // Only retry if shouldShowChat is true
        if (!shouldShowChat) {
          return;
        }
        
        // Continue retrying indefinitely, but log warnings periodically
        if (retryCount % 50 === 0 && retryCount > 0) {
          console.warn(`⏳ BeChat script not loaded after ${retryCount * 0.1} seconds. Still retrying...`);
          // Check if script tag exists
          const scriptTag = document.querySelector('script[src*="livechat-loader"]');
          if (scriptTag) {
            console.log("BeChat: Script tag found:", scriptTag.src);
          } else {
            console.warn("BeChat: Script tag not found, reloading...");
            loadBeChatScript();
          }
        }
        
        // Limit retries to avoid infinite loop (max 10 seconds)
        if (retryCount > 100) {
          console.error("❌ BeChat: Max retries reached (10 seconds), giving up");
          console.error("BeChat: Please check:");
          console.error(`1. Is ${BECHAT_WIDGET_URL} accessible?`);
          console.error("2. Check Network tab for script loading errors");
          console.error("3. Check browser console for CORS or other errors");
          return;
        }
        
        // Continue retrying indefinitely
        timeoutId = setTimeout(() => initBeChat(retryCount + 1), 100);
        return;
      }

      console.log("BeChat: Found, initializing", { shouldShowChat, isLogged, isExtendLayout, retryCount });

      // Register onLoaded callback only once
      if (!beChatInitializedRef.current && typeof beChat.onLoaded === "function") {
        console.log("BeChat: Registering onLoaded callback");
        beChatInitializedRef.current = true;
        beChat.onLoaded(() => {
          console.log("BeChat: onLoaded callback fired - syncing");
          // Use current values when callback fires
          const currentShouldShow = isExtendLayout && Boolean(isLogged);
          
          const syncNow = async () => {
            try {
              if (currentShouldShow) {
                const currentUserData = await buildBeChatUserData({
                  id,
                  email,
                  first_name,
                  last_name,
                  phone_number,
                });
                if (currentUserData && Object.keys(currentUserData).length > 0) {
                  await beChat.setUserData(currentUserData);
                }
                await beChat.showLauncher();
                
                // Fallback: Force show iframe if showLauncher didn't work
                // Try multiple times with increasing delays
                [500, 1000, 2000, 3000, 5000].forEach((delay) => {
                  setTimeout(() => {
                    const iframe = document.getElementById('be-chat-iframe');
                    if (iframe) {
                      const computedStyle = window.getComputedStyle(iframe);
                      if (iframe.style.display === 'none' || computedStyle.display === 'none') {
                        console.log(`BeChat: Fallback in onLoaded (${delay}ms) - forcing iframe to show`);
                        iframe.style.display = 'block';
                        iframe.style.visibility = 'visible';
                      }
                    }
                  }, delay);
                });
              } else {
                await beChat.hideLauncher();
              }
            } catch (error) {
              console.error("BeChat sync error:", error);
            }
          };
          syncNow();
        });
      }

      // Always sync immediately with current state (don't wait for onLoaded if already initialized)
      if (beChatInitializedRef.current || typeof beChat.onLoaded !== "function") {
        console.log("BeChat: Syncing immediately", { initialized: beChatInitializedRef.current });
        syncBeChat(beChat);
      }
    };

    initBeChat();

    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (observerInterval) {
        clearInterval(observerInterval);
      }
      if (styleTag && styleTag.parentNode) {
        styleTag.parentNode.removeChild(styleTag);
      }
    };
  }, [isExtendLayout, isLogged, id, email, first_name, last_name, phone_number]);

  return (
    <>
      {isExtendLayout ? (
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <Header onClickPopupOpen={onClickPopupOpen} onClickDrawerOpen={onClickDrawerOpen} isOpen={isOpen} />
          <Sidebar handlePopupClose={handlePopupClose} isOpen={isOpen} isPop={isPop} />
          <Box
            component="main"
            sx={{ flexGrow: 1, p: 3 }}
            style={{ overflow: "auto" }}
          >
            <Toolbar style={{ minHeight: 100 }} />
            {children}
          </Box>
        </Box>
      ) : (
        children
      )}
    </>
  );
};

export default React.memo(Layout);
